/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react'
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import api from 'api';
import { authToken } from 'lib/storagePolyfill';

import navigationActions from 'redux/actions/navigation';
import sessionActions from 'redux/actions/session';
import uiStateActions from 'redux/actions/uiState';
import groupSelectors from 'redux/reduxStore/group';
import userActions from 'redux/actions/user';
import userSelector from 'redux/reduxStore/user';
import localeSelector from 'redux/reduxStore/locale';
import listLocaleSelector from 'redux/reduxStore/lists/listLocale';

import SetPasswordPresenter from 'components/pages/token/SetPassword/SetPassword.presenter';
import LoginPasswordPresenter from 'components/pages/login/LoginPassword/LoginPassword.presenter';
import MfaCodeEntry from 'components/pages/mfaCode';

import routeNames from 'lib/routeNames';
import AppUtils from 'lib/AppUtils';
import { sessionDurationTypes } from 'models/AccessTokenModel';

function mapStateToProps(state, routerProps) {
  const defaultLanguageCode = localeSelector.getDefaultClientLocale(state);
  const localeList = listLocaleSelector.getCollection(state);
  const defaultLocale = localeList.filter(l => l.language_code === defaultLanguageCode)[0];
  return {
    email: AppUtils.getQueryParams(routerProps.location.search).e,
    groupId: groupSelectors.getActiveGroup(state).id,
    localeId: userSelector.getLocaleId(state) || defaultLocale.id,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    goBack: () => {
      dispatch(navigationActions.goBackTo(routeNames.loginEmail()));
    },
    goHome: () => {
      dispatch(navigationActions.goTo(routeNames.home()));
    },
    goToMissingInvite: () => {
      dispatch(navigationActions.goTo(routeNames.missingInvitation()));
    },
    goToPassword: () => {
      dispatch(navigationActions.goBackTo(routeNames.loginPassword()));
    },
    showApiError: (apiError) => {
      dispatch(uiStateActions.showApiError(apiError));
    },
    loginSuccessful: (session) => {
      dispatch(sessionActions.loginSuccessful(session));
    },
    updateUser: (user) => {
      dispatch(userActions.receive(user));
    },
    logout: () => {
      dispatch(sessionActions.logout());
    },
  }
}

class AcceptInvitationPresenter extends React.Component {

  constructor(props) {
    super(props);

    this.submitCredentials = this.submitCredentials.bind(this);
    this.setPassword = this.setPassword.bind(this);
    this.setPasswordMfa = this.setPasswordMfa.bind(this);
    this.acceptInvitation = this.acceptInvitation.bind(this);

    this.state = {
      isExistingUser: false,
      requiresMfa: false,
      errorPhraseId: '',
      setPasswordSuccess: false,
      passwordSubmitted: false,
      credentials: {},
    };
  }

  UNSAFE_componentWillMount() {
    if (!this.props.email) { return; }
    api.userApi.checkEmailExists(this.props.email)
      .then((isExistingUser) => { this.setState({ isExistingUser }); })
      .catch(() => {
        this.setState({ isExistingUser: false });
      });
  }

  acceptInvitation(groupCredentials, mfaCode) {
    let session;

    return api.notificationApi.getAll()
      .then((notifications) => {
        const groupId = this.props.groupId;
        const invitation = notifications.filter(n => n.type === 'group_admin').filter(n => n.group_admin.group_id === groupId)[0]

        if (invitation) {
          return api.notificationApi.accept(invitation.id)
        } else {
          const user = authToken.decode().user;
          this.props.logout();
          this.props.goToMissingInvite();
          this.props.updateUser(user);
          return Promise.reject({ missingInvitation: true });
        }
      }).then(() => api.sessionApi.logout())
      .then(() => api.sessionApi.login(groupCredentials))
      .then((s) => {
        session = s;
        if (session.type === 'mfa') {
          return api.sessionApi.mfaLogin(mfaCode, sessionDurationTypes.SHORT).then(() => {
            return session.user;
          });
        } else {
          const user = session.user;
          return api.userApi.get(user.id);
        }
      }).then((user) => {
        this.props.loginSuccessful(session);
        this.props.goHome();
        this.props.updateUser(user);
      }).catch(res => {
        if (!res.missingInvitation) {};
      });
  }

  submitCredentials(credentials) {
    const { groupId } = this.props;
    const groupCredentials = _.assign({}, credentials, { groupId, duration: sessionDurationTypes.SHORT });

    return api.sessionApi.login(credentials)
    .then((session) => {
      if (session.type === 'mfa') {
        this.setState({
          credentials,
          requiresMfa: true,
        });
        return Promise.reject();
      }
      return this.acceptInvitation(groupCredentials);
    }).catch((apiError) => {
      this.setState({ errorPhraseId: apiError.messageKey() });
    });
  }

  setPassword(token, password, isMfaRequired) {
    this.setState({
      passwordSubmitted: true,
    });

    const credentials = {
      email: this.props.email,
      password,
      locale_id: this.props.localeId,
      duration: sessionDurationTypes.SHORT,
    };

    api.userApi.create(credentials)
    .then(() => this.submitCredentials(credentials));
  }

  setPasswordMfa(mfaCode) {
    this.setState({
      passwordSubmitted: true,
    });

    const groupCredentials = _.assign({}, this.state.credentials, {
      groupId: this.props.groupId,
      duration: sessionDurationTypes.SHORT,
    });

    return api.sessionApi.mfaLogin(mfaCode, sessionDurationTypes.SHORT)
    .then((session) => this.acceptInvitation(groupCredentials, mfaCode));

  }

  render() {
    if (!this.props.email) {
      return <Redirect to={routeNames.loginEmail()} />;
    }

    if (this.state.requiresMfa) {
      return (
        <MfaCodeEntry
          submitMfaCode={(mfaCode) => this.setPasswordMfa(mfaCode)}
          goBack={this.props.goBack}
        />
      );
    } else if (this.state.isExistingUser) {
      return (
        <LoginPasswordPresenter
          submitCredentials={this.submitCredentials}
          requiresMfa={false}
          errorPhraseId={this.state.errorPhraseId}
          clearErrorMessage={() => { this.setState({ errorPhraseId: '' }) }}
          goBack={this.props.goBack}
          email={this.props.email}
        />
      );
    } else {
      return (
        <SetPasswordPresenter
          resetPassword={this.setPassword}
          resetPasswordMfa={this.setPasswordMfa}
          resetPasswordSuccess={this.state.setPasswordSuccess}
          passwordSubmitted={this.state.passwordSubmitted}
          cancelPassword={() => { this.setState({ passwordSubmitted: false }); }}
          titlePhrase={{ id: 'setPwd.title.text' }}
          isMfaRequired={false}
          email={this.props.email}
          goHome={this.props.goHome}
        />
      );
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AcceptInvitationPresenter);